import React, { useEffect, useState } from "react";
import {
  MdOutlineVisibility,
  MdOutlineMap,
  MdOutlineSecurity,
  MdOutlineTrendingUp,
} from "react-icons/md";
import "../styles/properties.css";
import InvestmentMap from "../components/InvestmentMap";
import { useTranslation } from "react-i18next";

const BACKEND_URL = "https://ddmwrl2uut5q8.cloudfront.net";
const INITIAL_PROPERTIES_TO_SHOW = 4;

function Properties() {
  const { t, i18n } = useTranslation(); // Initialize t and i18n
  const currentLanguage = i18n.language; // Get current language

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true); // Added loading state
  const [expandedGroups, setExpandedGroups] = useState({}); // To track which city groups are expanded

  useEffect(() => {
    setLoading(true); // Set loading true at the start of fetch
    fetch(`${BACKEND_URL}/api/properties`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) setProperties(data);
        else {
          console.error("Fetched data is not an array:", data);
          setProperties([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching properties:", error);
        setProperties([]);
      })
      .finally(() => {
        setLoading(false); // Set loading false when fetch completes or fails
      });
  }, []);

  // Group properties by city name (which should be translated if it comes from map data)
  const grouped = Array.isArray(properties)
    ? properties.reduce((acc, prop) => {
        // --- Assuming `prop.city` might be an object {en: "City EN", sq: "Qyteti SQ"} ---
        // --- OR it's a key like 'pristina' that you map to a translated name.
        // --- For now, let's assume `prop.mapData.name` (if available) or a `prop.cityNameTranslations` object is preferred.
        // --- If `prop.city` is a simple string from backend, it won't be translated here unless it's a key.
        let cityGroupName = t("propertiesPage.defaultCityGroupTitle"); // Default

        if (prop.cityInfo && typeof prop.cityInfo.name === 'object' && prop.cityInfo.name !== null) { // Example: if backend nests city info
            cityGroupName = prop.cityInfo.name[currentLanguage] || prop.cityInfo.name.en || prop.cityInfo.name.sq || cityGroupName;
        } else if (typeof prop.city === 'object' && prop.city !== null) { // If prop.city itself is the translation object
            cityGroupName = prop.city[currentLanguage] || prop.city.en || prop.city.sq || cityGroupName;
        } else if (prop.city) { // If prop.city is a string (could be a key or untranslated name)
            cityGroupName = prop.city; // Or use t(`cities.${prop.city}`) if it's a key
        }
        // For this example, we'll use the 'name' from Object.entries which is derived from this ^ logic.

        if (!acc[cityGroupName]) acc[cityGroupName] = [];
        acc[cityGroupName].push(prop);
        return acc;
      }, {})
    : {};

  const toggleGroupExpansion = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  if (loading) {
    return (
      <section className="properties-page-pro">
        <InvestmentMap />
        <div className="container page-content-after-hero">
          <p className="loading-message">{t("propertiesPage.loadingProperties")}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="properties-page-pro">
      <InvestmentMap /> {/* Consider if InvestmentMap also needs loading state or shows its own */}
      <div className="container page-content-after-hero">
        <section className="properties-benefits-pro">
          <h3>{t("propertiesPage.benefits.title")}</h3>
          <div className="benefits-grid">
            {/* Benefit items remain the same, as they use static translations */}
            <div className="benefit-item">
              <MdOutlineMap className="benefit-icon" />
              <h4>{t("propertiesPage.benefits.strategic.title")}</h4>
              <p>{t("propertiesPage.benefits.strategic.description")}</p>
            </div>
            <div className="benefit-item">
              <MdOutlineTrendingUp className="benefit-icon" />
              <h4>{t("propertiesPage.benefits.growth.title")}</h4>
              <p>{t("propertiesPage.benefits.growth.description")}</p>
            </div>
            <div className="benefit-item">
              <MdOutlineVisibility className="benefit-icon" />
              <h4>{t("propertiesPage.benefits.visibility.title")}</h4>
              <p>{t("propertiesPage.benefits.visibility.description")}</p>
            </div>
            <div className="benefit-item">
              <MdOutlineSecurity className="benefit-icon" />
              <h4>{t("propertiesPage.benefits.secure.title")}</h4>
              <p>{t("propertiesPage.benefits.secure.description")}</p>
            </div>
          </div>
        </section>

        <section className="properties-listing">
          {Object.keys(grouped).length === 0 && !loading && ( // Show no properties message only if not loading
            <p className="no-properties-message">
              {t("propertiesPage.listing.noProperties")}
            </p>
          )}
          {Object.entries(grouped).map(([groupName, propsInGroup]) => {
            const isExpanded = expandedGroups[groupName];
            const propertiesToShow = isExpanded ? propsInGroup : propsInGroup.slice(0, INITIAL_PROPERTIES_TO_SHOW);

            return (
              <div key={groupName} className="city-group">
                <h2 className="city-heading">{groupName}</h2> {/* Assumes groupName is already translated or is a direct display name */}
                <div className="property-grid-pro">
                  {propertiesToShow.map((prop) => {
                    // --- Logic to get translated dynamic content from 'prop' ---
                    const displayTitle =
                      typeof prop.title === "object" && prop.title !== null
                        ? prop.title[currentLanguage] || prop.title.en || prop.title.sq || t("propertiesPage.listing.card.defaultTitle")
                        : prop.title || t("propertiesPage.listing.card.defaultTitle");

                    const displayPurpose =
                      typeof prop.purpose === "object" && prop.purpose !== null
                        ? prop.purpose[currentLanguage] || prop.purpose.en || prop.purpose.sq || t("propertiesPage.listing.card.notAvailable")
                        : prop.purpose || t("propertiesPage.listing.card.notAvailable");

                    const imageAltText = displayTitle !== t("propertiesPage.listing.card.defaultTitle")
                                        ? displayTitle
                                        : t("propertiesPage.listing.card.defaultImageAlt");

                    // Translating status for display
                    const displayStatus = prop.status
                        ? t(`propertiesPage.status.${prop.status.toLowerCase().replace(/\s+/g, '_')}`, prop.status.charAt(0).toUpperCase() + prop.status.slice(1))
                        : t("propertiesPage.status.unknown");

                    return (
                      <div className="property-card-pro" key={prop.id}>
                        <div className="card-image">
                          <img
                            src={
                              prop.image
                                ? `${BACKEND_URL}${prop.image}`
                                : "/images/placeholder-property.jpg"
                            }
                            title={imageAltText} // Use translated title for image title
                            alt={imageAltText}
                          />
                          {prop.status && (
                            <span
                              className={`card-status-badge status-${prop.status
                                .toLowerCase()
                                .replace(/[\s_]/g, "-")}`}
                            >
                              {displayStatus} {/* Display translated status */}
                            </span>
                          )}
                        </div>
                        <div className="card-content">
                          <h3 className="card-title">
                            {displayTitle}
                          </h3>
                          <div className="card-details">
                            <p>
                              <span className="detail-label">{t("propertiesPage.listing.card.purposeLabel")}</span>{" "}
                              {displayPurpose}
                            </p>
                            {/* You might want to add more details here like area, price, etc.
                                Ensure those fields (e.g., prop.area, prop.price_info) are also handled for multilingual display if needed.
                                For example, prop.area might be fine as is, but prop.price_info.lease_term (if it's an object {en, sq}) needs:
                                prop.price_info.lease_term[currentLanguage] || prop.price_info.lease_term.en ...
                            */}
                          </div>
                          <div className="card-action">
                            <a
                              href={`/properties/${prop.id}`}
                              className="view-button-pro"
                            >
                              {t("propertiesPage.listing.card.viewDetailsButton")}
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {propsInGroup.length > INITIAL_PROPERTIES_TO_SHOW && (
                  <div className="show-more-container">
                    <button
                      onClick={() => toggleGroupExpansion(groupName)}
                      className="show-more-button"
                    >
                      {isExpanded ? t("propertiesPage.listing.showLess") : t("propertiesPage.listing.showMore")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>
    </section>
  );
}

export default Properties;